import React, {PropsWithChildren} from "react"
import {useNavigate} from "react-router-dom"
import {useModal} from "react-modal-hook"
import {startCase} from "lodash"
import classNames from "classnames"
import ReevaluateApplicationCancelingModal from "../../components/ReevaluateApplication/ReevaluateApplicationCancelingModal/ReevaluateApplicationCancelingModal"
import {Application} from "../../resources/application"
import {applicationStatusTheme} from "../../components/Tag"
import TitleBar, {MetaItemProps, TitleBarAction} from "../../components/TitleBar/TitleBar"
import Button from "../../components/Button/Button"
import {MainSection} from "../../containers/MainSection/MainSection"
import {OkDocument} from "../../resources/common"
import {useListReturnPath} from "../../hooks/useListReturnPath"

type ApplicationDetailsProps = {
	application: OkDocument<Application>
	title: string
	meta: Array<MetaItemProps>
	formId: string
}

export default function ApplicationDetails({
	children,
	application,
	title,
	meta,
	formId,
}: PropsWithChildren<ApplicationDetailsProps>) {
	const listReturnPath = useListReturnPath("/applications/")
	const navigate = useNavigate()

	const applicationStatus = application.data.attributes.status

	const [showCancelingModal, closeClosingModal] = useModal(() => (
		<ReevaluateApplicationCancelingModal
			close={closeClosingModal}
			onSavingCallback={() => navigate(`/applications/${application.data.id}`)}
		/>
	))

	return (
		<div className="reevaluate-page">
			<TitleBar
				title={title}
				breadcrumbs={[{text: "Applications", path: listReturnPath}]}
				meta={meta}
				status={{
					text: startCase(applicationStatus),
					className: classNames(
						"unit-tag",
						"outline",
						applicationStatusTheme(applicationStatus),
						"large",
						"title-header-reevaluate"
					),
				}}
			>
				<TitleBarAction>
					<Button
						className="mr-3"
						prefixIcon="delete-close-x"
						size="normal"
						theme="danger"
						variant="outline"
						onClick={showCancelingModal}
					>
						<span>Cancel</span>
					</Button>

					<Button
						form={formId}
						type="submit"
						prefixIcon="approve-v-check-validation"
						size="normal"
						theme="success"
						variant="outline"
					>
						<span>Save changes</span>
					</Button>
				</TitleBarAction>
			</TitleBar>
			<MainSection>
				<React.Fragment>{children}</React.Fragment>
			</MainSection>
		</div>
	)
}

interface IdentificationFields {
	ssn?: string
	passport?: string
	matriculaConsular?: string
}

interface IdentificationFieldPrepareBeforeUdpateRequest {
	currentValues: IdentificationFields
	updatedValues: IdentificationFields
}

export function identificationFieldBeforeUpdateRequest({
	currentValues,
	updatedValues,
}: IdentificationFieldPrepareBeforeUdpateRequest): IdentificationFields {
	const {ssn, passport, matriculaConsular} = currentValues

	return {
		ssn: updatedValues.ssn
			? updatedValues.ssn
			: ssn && (updatedValues.passport || updatedValues.matriculaConsular)
			? undefined
			: ssn,
		passport: updatedValues.passport
			? updatedValues.passport
			: passport && (updatedValues.ssn || updatedValues.matriculaConsular)
			? undefined
			: passport,
		matriculaConsular: updatedValues.matriculaConsular
			? updatedValues.matriculaConsular
			: matriculaConsular && (updatedValues.ssn || updatedValues.passport)
			? undefined
			: matriculaConsular,
	}
}
